import React, { FunctionComponent, memo, useCallback } from 'react';
import { Field, useFormikContext } from 'formik';
import FormikOutreachConfigField from './controls/OutreachConfig/FormikOutreachConfigField';
import {
  OutreachConfigFormFields,
  OutreachConfigSections,
  OutreachTriggerTypes,
  SupportOutreachTriggerFormFields,
} from '../../types';
import FormikOutreachSubscriberTimeField from './controls/OutreachConfig/subscriber/FormikOutreachSubscriberTimeField';
import FormikSubscriberOutreachTriggerField from './controls/OutreachConfig/subscriber/FormikSubscriberOutreachTriggerField';
import FormikOutreachSupportTimeField from './controls/OutreachConfig/support/FormikOutreachSupportTimeField';
import FormikSupportOutreachTriggerField from './controls/OutreachConfig/support/FormikSupportOutreachTriggerField';
import FormikSupportOutreachContentField from './controls/OutreachConfig/support/FormikSupportOutreachContentField';
import FormikSupportOutreachClearMessageField from './controls/OutreachConfig/support/FormikSupportOutreachClearMessageField';
import OutreachTestMessageModal from '../../../Outreach/components/OutreachTestMessageModal';
import { outreachTestMessageModalAtom } from '../../../../state';
import { useRecoilState } from 'recoil';
import { OutreachTypes } from '../../../../urls';
import { OutreachConfigFormDto } from '../../../Outreach/hooks/useOutreach';
import FormikSubscriberOutreachClearMessageField from './controls/OutreachConfig/subscriber/FormikSubscriberOutreachClearMessageField';
import FormikSupportOutreachTriggerWebhookField from './controls/OutreachConfig/support/FormikSupportOutreachTriggerWebhookField';
import FormikOutreachSupportTimeFieldWebhook from './controls/OutreachConfig/support/FormikOutreachSupportTimeWebhook';
import FormikSubscriberOutreachContentField from './controls/OutreachConfig/subscriber/FormikSubscriberOutreachContentField';
import { useLocation } from 'react-router-dom';

const shouldShowClearSectionFor = (
  outreachType: OutreachTypes,
  values: OutreachConfigFormDto,
) => {
  const sectionMap: Record<
    OutreachTypes,
    | OutreachConfigSections.SupportOutreachTrigger
    | OutreachConfigSections.SubscriberOutreachTrigger
  > = {
    [OutreachTypes.Email]: OutreachConfigSections.SupportOutreachTrigger,
    [OutreachTypes.Webhooks]: OutreachConfigSections.SupportOutreachTrigger,
    [OutreachTypes.Push]: OutreachConfigSections.SubscriberOutreachTrigger,
  };
  const triggerSectionName = sectionMap[outreachType];
  const trigger = values[triggerSectionName];
  return Boolean(
    trigger[SupportOutreachTriggerFormFields.TriggerType] !==
      OutreachTriggerTypes.Time &&
      trigger[SupportOutreachTriggerFormFields.SendClearEmailWhenIssueCleared],
  );
};

const OutreachConfigForm: FunctionComponent = () => {
  const { values } = useFormikContext();
  const { outreachConfig } = values as any;
  const shouldShowSupportOutreachSections = Boolean(
    outreachConfig[OutreachConfigFormFields.SupportOutreach],
  );
  const shouldShowSupportClearMessageSection = shouldShowClearSectionFor(
    OutreachTypes.Email,
    values as OutreachConfigFormDto,
  );
  const shouldShowSubscriberOutreachSections = Boolean(
    outreachConfig[OutreachConfigFormFields.SubscriberOutreach],
  );
  const shouldShowWebhookOutreachConfigSections = Boolean(
    outreachConfig[OutreachConfigFormFields.WebhookOutreach],
  );
  const shouldShowSubscriberClearMessageSection = shouldShowClearSectionFor(
    OutreachTypes.Push,
    values as OutreachConfigFormDto,
  );
  const [
    outreachTestMessageModal,
    setOutreachTestMessageModal,
  ] = useRecoilState(outreachTestMessageModalAtom);
  const { state } = useLocation();
  const scroll = useCallback((node: HTMLDivElement) => {
    if (node === null) return;
    node.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  return (
    <>
      <div ref={state?.anchor === 'outreach-enable' ? scroll : null} />
      <Field
        name={OutreachConfigSections.Config}
        component={FormikOutreachConfigField}
      />
      {shouldShowSubscriberOutreachSections && (
        <>
          <div ref={state?.anchor === 'subscriber-outreach' ? scroll : null} />
          <Field
            name={OutreachConfigSections.SubscriberOutreachTime}
            component={FormikOutreachSubscriberTimeField}
          />
          <Field
            name={OutreachConfigSections.SubscriberOutreachTrigger}
            component={FormikSubscriberOutreachTriggerField}
          />
          <Field
            name={OutreachConfigSections.SubscriberOutreachContent}
            component={FormikSubscriberOutreachContentField}
          />
          {shouldShowSubscriberClearMessageSection && (
            <Field
              name={OutreachConfigSections.SubscriberOutreachClearMessage}
              component={FormikSubscriberOutreachClearMessageField}
            />
          )}
        </>
      )}
      {shouldShowSupportOutreachSections && (
        <>
          <div ref={state?.anchor === 'support-outreach' ? scroll : null} />
          <Field
            name={OutreachConfigSections.SupportOutreachTime}
            component={FormikOutreachSupportTimeField}
          />
          <Field
            name={OutreachConfigSections.SupportOutreachTrigger}
            component={FormikSupportOutreachTriggerField}
          />
          <Field
            name={OutreachConfigSections.SupportOutreachContent}
            component={FormikSupportOutreachContentField}
          />
          {shouldShowSupportClearMessageSection && (
            <Field
              name={OutreachConfigSections.SupportOutreachClearMessage}
              component={FormikSupportOutreachClearMessageField}
            />
          )}
        </>
      )}
      {shouldShowWebhookOutreachConfigSections && (
        <>
          <div ref={state?.anchor === 'webhook-outreach' ? scroll : null} />
          <Field
            name={OutreachConfigSections.SupportOutreachTimeWebhook}
            component={FormikOutreachSupportTimeFieldWebhook}
          />
          <Field
            name={OutreachConfigSections.SupportOutreachTriggerWebhook}
            component={FormikSupportOutreachTriggerWebhookField}
          />
        </>
      )}
      <OutreachTestMessageModal
        isOpen={outreachTestMessageModal.isOpen}
        onRequestClose={() =>
          setOutreachTestMessageModal((m) => ({
            ...m,
            isOpen: false,
          }))
        }
      />
    </>
  );
};

export default memo(OutreachConfigForm);
